/**
 * Easing curves for scene transitions and layer animation.
 * Pure functions over t in [0, 1]; inputs outside the range are clamped so a
 * caller that overshoots by a frame never gets a wild value.
 */

import type { EasingId } from '@canvas/shared-types';

function clamp01(t: number): number {
  if (!Number.isFinite(t)) return 0;
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

/** Overshoot constant for back-out; the common Penner value (~10% overshoot). */
const BACK_OVERSHOOT = 1.70158;

/** Easing function table keyed by the serializable EasingId. */
export const EASINGS: Record<EasingId, (t: number) => number> = {
  linear: (t) => clamp01(t),
  'ease-in': (t) => {
    const x = clamp01(t);
    return x * x * x;
  },
  'ease-out': (t) => {
    const x = 1 - clamp01(t);
    return 1 - x * x * x;
  },
  'ease-in-out': (t) => {
    const x = clamp01(t);
    return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
  },
  smoothstep: (t) => {
    const x = clamp01(t);
    return x * x * (3 - 2 * x);
  },
  'back-out': (t) => {
    const x = clamp01(t) - 1;
    return 1 + (BACK_OVERSHOOT + 1) * x * x * x + BACK_OVERSHOOT * x * x;
  },
  'spring-soft': (t) => {
    const x = clamp01(t);
    if (x === 0 || x === 1) return x;
    // Damped cosine: settles at 1 with one gentle overshoot.
    return 1 - Math.exp(-6 * x) * Math.cos(x * Math.PI * 2.5);
  },
};

/** Apply a named easing; unknown ids fall back to linear. */
export function ease(id: EasingId | undefined, t: number): number {
  const fn = (id && EASINGS[id]) || EASINGS.linear;
  return fn(t);
}
